<template>
    <div class="airport-search-input">
        <template v-if="!newVersion">
            <input type="hidden" ref="hiddenInput" :name="name" :value="value" />
            <md-field
                class="with-left-icon"
                v-bind:class="{ 'md-invalid': !!inputError }"
            >
                <label v-if="!noLabel">{{ label }}</label>
                <!-- Do not remove. This label is required to disable Chrome's autofill feature -->
                <label style="display: none">disable autofill</label>
                <custom-icon v-if="!noIcon" class="md-prefix" :name="useIcon" />
                <md-input
                    type="text"
                    v-model="selectedcityName"
                    ref="searchInput"
                    :placeholder="label + (required ? ' *' : '')"
                    v-on:blur="onBlur($event)"
                    :disabled="disabled"
                    dusk="city-search-input"
                    :autofocus="autofocus"
                    id="autocomplete-search-input"
                    :required="required"
                />
                <md-progress-spinner
                    v-show="searching"
                    md-mode="indeterminate"
                    :md-diameter="20"
                    :md-stroke="3"
                />
                <span class="md-error">{{ inputError ? inputError[0] : "" }}</span>
            </md-field>
        </template>
        <template v-else>
            <input type="hidden" ref="hiddenInput" :name="name" :value="value" />
            <div class=" CityInputComponent">
                <div class="relative">
                    <!-- Do not remove. This label is required to disable Chrome's autofill feature -->
                    <label style="display: none">disable autofill</label>
                    <div class="absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none" v-if="!!icon">
                        <font-awesome-icon :icon="['fas', icon]" :style="{ color: '#a0a0a0' }" />
                    </div>
                    <input
                        type="text"
                        v-model="selectedcityName"
                        ref="searchInput"
                        :placeholder="label + (required ? ' *' : '')"
                        @blur="onBlur($event)"
                        :disabled="disabled"
                        dusk="city-search-input"
                        :autofocus="autofocus"
                        id="autocomplete-search-input"
                        :required="required"
                        class="placeholder:italic block bg-white border border-gray-200 !text-primary-dark-50 text-sm rounded-md block w-full p-1.5"
                        :class="{ 'border-red-500 focus:border-red-500 focus:ring-red-500': !!inputError, 'ps-10': !!icon }"
                    >
                    <md-progress-spinner
                        v-show="searching"
                        md-mode="indeterminate"
                        :md-diameter="20"
                        :md-stroke="3"
                        class="!top-1.5"
                    />
                </div>
                <p class="mt-2 text-xs text-red-500" v-if="inputError">{{ inputError ? inputError[0] : "" }}</p>
            </div>
        </template>
    </div>
</template>

<script>
export default {
    props: {
        name: String,
        label: String,
        value: Number,
        cityName: String,
        icon: String,
        noLabel: Boolean,
        inputError: Array,
        disabled: String,
        autofocus: String,
        noIcon: {
            type: Boolean,
            default: false,
        },
        required: Boolean,
        newVersion: {
            type: Boolean,
            default: false,
        }
    },
    data: () => {
        return {
            searchTimeout: null,
            source: null,
            typeahead: null,
            storedcityName: null,
            overridecityName: null,
            searching: false,
            limit: 10,
            touched: false,
        };
    },
    computed: {
        selectedcityName: {
            get() {
                if (this.cityName && this.overridecityName === null) {
                    this.overridecityName = this.cityName;
                }
                return this.overridecityName !== null
                    ? this.overridecityName
                    : this.cityName;
            },
            set(value) {
                this.overridecityName = value;
            },
        },
        useIcon() {
            return this.icon ? this.icon : "flight_takeoff";
        },
    },
    mounted() {
        this.storedcityName = this.cityName;
        this.initSource();
        this.initTypeahead();
    },
    methods: {
        initSource() {
            this.source = (query, syncResults, asyncResults) => {
                if (this.touched) {
                    if (this.searchTimeout) {
                        clearTimeout(this.searchTimeout);
                    }
                    this.searching = true;
                    this.searchTimeout = setTimeout(() => {
                        let finalResults = [];
                        axios.get(
                            '/api/cities',
                            {
                                params: {
                                    term: query,
                                    per_page: this.limit - finalResults.length,
                                    disable_pagination: 1,
                                    with_country: 1,
                                },
                            }
                        ).then((response) => {
                            finalResults = finalResults.concat(
                                response.data.map((c) => {
                                    c['type'] = 'city';
                                    c['icon'] = 'location_city';
                                    c['label'] = Vue.i18n.translate(
                                        'registration.search-city',
                                        {
                                            city: c['name_list'].split(
                                                ','
                                            )[0],
                                            department_country:
                                                (c['adminLevel2']
                                                    ? c['adminLevel2'][
                                                        'name'
                                                    ] + ', '
                                                    : c['adminLevel1']
                                                        ? c['adminLevel1'][
                                                            'name'
                                                        ] + ', '
                                                        : c['postalCode']
                                                            ? c['postalCode'] + ', '
                                                            : '') +
                                                c['country']['nom'][this.$i18n.locale()],
                                        }
                                    );
                                    return c;
                                })
                            );
                            asyncResults(finalResults);
                            this.searching = false;
                        });
                    }, 300);
                }
            };
        },
        initTypeahead() {
            const el = this.newVersion ? this.$refs.searchInput : this.$refs.searchInput.$el;
            this.typeahead = $(el).typeahead(null, {
                display: 'label',
                source: this.source,
                templates: {
                    suggestion: Handlebars.compile(
                        '<div><i class="material-icons">{{icon}}</i> {{label}}</div>'
                    ),
                },
                hint: false,
                minLength: 2,
                limit: 2 * this.limit,
            });
            this.typeahead.bind("typeahead:select", (event, suggestion) => {
                this.$emit("input", suggestion["id"]);
                this.selectedcityName = suggestion["label"];
                this.storedcityName = suggestion["label"];
            });
        },
        onBlur(event) {
            if (!this.value || this.overridecityName === "") {
                this.$emit("input", null);
                this.storedcityName = "";
                this.overridecityName = "";
            } else if (this.overridecityName !== this.storedcityName) {
                this.overridecityName = this.storedcityName;
                this.typeahead.typeahead("val", "");
            }
            this.touched = false;
        },
        clearAndFocus() {
            this.selectedcityName = "";
            this.$refs.searchInput.$el.focus();
        },
    },
    watch: {
        selectedcityName(value) {
            this.touched = true;
        },
    },
};
</script>
<style lang="scss">
@import "~styles/_variables";
#autocomplete-search-input {
    color: $primary-color-dark;
}
.airport-search-input:not(:has(.CityInputComponent)) {
    #autocomplete-search-input::placeholder {
        color: $primary-color-dark;
        font-size: 16px;
        @media (max-width: $mobile-top-breakpoint) {
            font-size: 12px;
        }
        line-height: 20px;
    }
}
.CityInputComponent {
    input:focus,
    input:focus-visible {
        outline: none !important;
        box-shadow: none !important;
        border-color: $primary-color-dark !important;
    }
    input {
        color: $primary-color-dark;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/widgets/CityInputComponent.vue